import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ItemLotEntity } from './item-lot.entity';
import { ItemEntity } from './item.entity';
import { LocationEntity } from './location.entity';
import { UserEntity } from './user.entity';
import { WarehouseEntity } from './warehouse.entity';

@Index('idx_inv_movement_item_date', ['fkItemId', 'createdDate'])
@Index('idx_inv_movement_warehouse_date', ['fkWarehouseId', 'createdDate'])
@Index('idx_inv_movement_type_date', ['type', 'createdDate'])
@Index('idx_inv_movement_reference', ['referenceType', 'referenceId'])
@Index('idx_inv_movement_lot', ['fkItemLotId'])
@Index('idx_inv_movement_location', ['fkLocationId', 'createdDate'])
@Entity('inventory_movement')
export class InventoryMovementEntity extends BaseEntity {
	/**
	 * Movement type — determines the nature of the inventory change.
	 * Stored as varchar (not MySQL enum) for extensibility.
	 * Values correspond to InventoryMovementType enum.
	 */
	@Column('varchar', { name: 'type', length: 32 })
	public type!: string;

	/** The item whose quantity changed */
	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	/** Warehouse context — which warehouse was affected (nullable for location-only movements) */
	@Column('int', { name: 'fk_warehouse__id', unsigned: true, nullable: true })
	public fkWarehouseId!: number | null;

	/** Location context — vehicle, warehouse location, or merchandiser (nullable for warehouse-level movements) */
	@Column('int', { name: 'fk_location__id', unsigned: true, nullable: true })
	public fkLocationId!: number | null;

	/** Lot context — which specific lot was affected (nullable for aggregate-level movements) */
	@Column('int', { name: 'fk_item_lot__id', unsigned: true, nullable: true })
	public fkItemLotId!: number | null;

	/** Quantity changed — positive for inbound, negative for outbound */
	@Column('int', { name: 'quantityChange' })
	public quantityChange!: number;

	/** Quantity on the affected entity before this movement (snapshot for audit) */
	@Column('int', { name: 'quantityBefore', nullable: true })
	public quantityBefore!: number | null;

	/** Quantity on the affected entity after this movement (snapshot for audit) */
	@Column('int', { name: 'quantityAfter', nullable: true })
	public quantityAfter!: number | null;

	/**
	 * Type of source document that triggered this movement.
	 * Values correspond to InventoryMovementReferenceType enum.
	 */
	@Column('varchar', { name: 'referenceType', length: 64, nullable: true })
	public referenceType!: string | null;

	/** ID of the source document (purchase order, order, shipment, etc.) */
	@Column('int', { name: 'referenceId', unsigned: true, nullable: true })
	public referenceId!: number | null;

	/** External system ID for cross-referencing with ERP or other systems */
	@Column('varchar', {
		name: 'referenceExternalId',
		length: 255,
		nullable: true,
	})
	public referenceExternalId!: string | null;

	/** Human-readable reason or notes for this movement */
	@Column('varchar', { name: 'reason', length: 512, nullable: true })
	public reason!: string | null;

	/** User who triggered this movement (may differ from creatorUserId for system-initiated movements) */
	@Column('int', {
		name: 'fk_triggered_by_user__id',
		unsigned: true,
		nullable: true,
	})
	public fkTriggeredByUserId!: number | null;

	// --- Relations (createForeignKeyConstraints: false to avoid circular dependency issues) ---

	@ManyToOne(() => ItemEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public item!: ItemEntity;

	@ManyToOne(() => WarehouseEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_warehouse__id', referencedColumnName: 'id' }])
	public warehouse!: WarehouseEntity | null;

	@ManyToOne(() => LocationEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_location__id', referencedColumnName: 'id' }])
	public location!: LocationEntity | null;

	@ManyToOne(() => ItemLotEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_item_lot__id', referencedColumnName: 'id' }])
	public itemLot!: ItemLotEntity | null;

	@ManyToOne(() => UserEntity, { createForeignKeyConstraints: false })
	@JoinColumn([
		{ name: 'fk_triggered_by_user__id', referencedColumnName: 'id' },
	])
	public triggeredByUser!: UserEntity | null;
}
